import React from 'react';
import { ScienceDebugger, DiagnosticStep } from './ScienceDebugger';

export const StorageScience: React.FC = () => {
  const storageSteps: DiagnosticStep[] = [
    {
      id: 'localstorage-available',
      label: 'LocalStorage is available',
      category: 'Core',
      critical: true,
      check: () => {
        try {
          const testKey = '__storage_test__';
          localStorage.setItem(testKey, 'test');
          const value = localStorage.getItem(testKey);
          localStorage.removeItem(testKey);
          return {
            passed: value === 'test',
            details: {
              available: true,
              test: 'read/write successful',
            }
          };
        } catch (error) {
          return {
            passed: false,
            error: 'LocalStorage not available (private browsing?)',
            details: {
              errorType: error instanceof Error ? error.name : 'Unknown',
            }
          };
        }
      }
    },
    {
      id: 'storage-quota',
      label: 'Check storage quota',
      category: 'Capacity',
      check: async () => {
        try {
          if ('storage' in navigator && 'estimate' in navigator.storage) {
            const estimate = await navigator.storage.estimate();
            const usagePercent = estimate.usage && estimate.quota
              ? (estimate.usage / estimate.quota) * 100
              : 0;

            return {
              passed: usagePercent < 90,
              details: {
                usage: estimate.usage,
                quota: estimate.quota,
                usagePercent: usagePercent.toFixed(2) + '%',
                usageMB: estimate.usage ? (estimate.usage / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown',
                quotaMB: estimate.quota ? (estimate.quota / 1024 / 1024).toFixed(2) + ' MB' : 'Unknown',
              }
            };
          }
          return {
            passed: true,
            details: { note: 'Storage API not available' }
          };
        } catch {
          return {
            passed: true,
            details: { note: 'Could not estimate storage' }
          };
        }
      }
    },
    {
      id: 'localstorage-size',
      label: 'LocalStorage usage analysis',
      category: 'Capacity',
      check: () => {
        try {
          let totalSize = 0;
          const items: Record<string, number> = {};

          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key) {
              const value = localStorage.getItem(key) || '';
              const itemSize = key.length + value.length;
              totalSize += itemSize;
              items[key] = itemSize;
            }
          }

          const sortedItems = Object.entries(items)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5);

          return {
            passed: totalSize < 5 * 1024 * 1024, // Less than 5MB
            details: {
              totalKeys: localStorage.length,
              totalSize: totalSize,
              totalSizeKB: (totalSize / 1024).toFixed(2) + ' KB',
              top5Items: sortedItems.map(([key, size]) => ({
                key,
                sizeKB: (size / 1024).toFixed(2) + ' KB',
              })),
            }
          };
        } catch (error) {
          return {
            passed: false,
            error: 'Could not analyze localStorage',
          };
        }
      }
    },
    {
      id: 'sessionstorage-available',
      label: 'SessionStorage is available',
      category: 'Core',
      check: () => {
        try {
          const testKey = '__session_test__';
          sessionStorage.setItem(testKey, 'test');
          const value = sessionStorage.getItem(testKey);
          sessionStorage.removeItem(testKey);
          return {
            passed: value === 'test',
            details: {
              available: true,
              itemCount: sessionStorage.length,
            }
          };
        } catch {
          return {
            passed: false,
            error: 'SessionStorage not available',
          };
        }
      }
    },
    {
      id: 'cookies-enabled',
      label: 'Cookies are enabled',
      category: 'Core',
      check: () => {
        const cookiesEnabled = navigator.cookieEnabled;
        const cookieCount = document.cookie ? document.cookie.split(';').length : 0;

        return {
          passed: cookiesEnabled,
          details: {
            enabled: cookiesEnabled,
            cookieCount,
            cookies: document.cookie ?
              document.cookie.split(';').map(c => c.trim().split('=')[0]) : [],
          }
        };
      }
    },
    {
      id: 'indexeddb-available',
      label: 'IndexedDB is available',
      category: 'Advanced',
      check: async () => {
        try {
          const testDB = '__test_db__';
          const request = indexedDB.open(testDB);

          return new Promise((resolve) => {
            request.onsuccess = () => {
              request.result.close();
              indexedDB.deleteDatabase(testDB);
              resolve({
                passed: true,
                details: {
                  available: true,
                  version: request.result.version,
                }
              });
            };

            request.onerror = () => {
              resolve({
                passed: false,
                error: 'IndexedDB not available',
              });
            };

            // Timeout after 2 seconds
            setTimeout(() => {
              resolve({
                passed: false,
                error: 'IndexedDB test timeout',
              });
            }, 2000);
          });
        } catch {
          return {
            passed: false,
            error: 'IndexedDB not supported',
          };
        }
      }
    },
    {
      id: 'storage-persistence',
      label: 'Check persistent storage',
      category: 'Advanced',
      check: async () => {
        try {
          if ('storage' in navigator && 'persist' in navigator.storage) {
            const isPersisted = await navigator.storage.persisted();

            return {
              passed: true,
              details: {
                isPersisted,
                canPersist: 'persist' in navigator.storage,
              }
            };
          }
          return {
            passed: true,
            details: { note: 'Persistence API not available' }
          };
        } catch {
          return {
            passed: true,
            details: { note: 'Could not check persistence' }
          };
        }
      }
    },
    {
      id: 'auth-tokens',
      label: 'Check auth token storage',
      category: 'Auth',
      check: () => {
        const authToken = localStorage.getItem('authToken');
        const authUser = localStorage.getItem('authUser');

        let userParsed = null;
        if (authUser) {
          try {
            userParsed = JSON.parse(authUser);
          } catch {}
        }

        return {
          passed: true, // Informational
          details: {
            hasToken: !!authToken,
            tokenLength: authToken?.length || 0,
            hasUser: !!authUser,
            userId: userParsed?.id,
            userEmail: userParsed?.email,
          }
        };
      }
    },
    {
      id: 'whatsapp-templates',
      label: 'Check WhatsApp template storage',
      category: 'App Data',
      check: () => {
        const templates = localStorage.getItem('wa_bot_templates_v1');
        const selectedId = localStorage.getItem('wa_bot_selected_template_id_v1');
        const number = localStorage.getItem('wa_bot_number_v1');

        let templatesParsed = null;
        if (templates) {
          try {
            templatesParsed = JSON.parse(templates);
          } catch {}
        }

        return {
          passed: true, // Informational
          details: {
            hasTemplates: !!templates,
            templateCount: Array.isArray(templatesParsed) ? templatesParsed.length : 0,
            hasSelectedTemplate: !!selectedId,
            hasPhoneNumber: !!number,
          }
        };
      }
    },
    {
      id: 'storage-events',
      label: 'Storage event listener works',
      category: 'Events',
      check: () => {
        try {
          let canListen = false;
          const testListener = () => {};

          window.addEventListener('storage', testListener);
          window.removeEventListener('storage', testListener);
          canListen = true;

          return {
            passed: canListen,
            details: {
              canListenToStorageEvents: canListen,
            }
          };
        } catch {
          return {
            passed: false,
            error: 'Cannot add storage event listener',
          };
        }
      }
    },
  ];

  return (
    <ScienceDebugger
      title="💾 Storage Science"
      description="Diagnose browser storage and persistence issues"
      steps={storageSteps}
      groupByCategory={true}
      animationDelay={200}
    />
  );
};